<template>
    <ValidationObserver ref="contactInfo">
        <div class="grid contact-info__container">
            <div class="grid__col-12 contact-info__title">
                <h2 class="ls-condensed section-title">
                    {{ title || `Create your ${brand} account` }}
                </h2>
            </div>
            <div class="grid__col-sm-5">
                <label class="ls-label ls-margin--top" for="fname">First Name</label>
                <div class="ls-input__wrapper">
                    <ValidationProvider v-slot="{ classes }" ref="fname" rules="required">
                        <input
                            id="fname"
                            v-model.trim="fname"
                            v-ls-track.change="{
                                target: 'contact-info--fname-input',
                                attachValue: true,
                            }"
                            type="text"
                            maxlength="50"
                            class="ls-input"
                            :class="classes"
                            name="fname"
                            autocomplete="given-name"
                            @input="checkValidationAndUpdate('fname')"
                        />
                    </ValidationProvider>
                </div>
            </div>
            <div class="grid__col-sm-5">
                <label class="ls-label ls-margin--top" for="lname">Last Name</label>
                <div class="ls-input__wrapper">
                    <ValidationProvider v-slot="{ classes }" ref="lname" rules="required">
                        <input
                            id="lname"
                            v-model.trim="lname"
                            v-ls-track.change="{
                                target: 'contact-info--lname-input',
                                attachValue: true,
                            }"
                            type="text"
                            maxlength="50"
                            class="ls-input"
                            :class="classes"
                            name="lname"
                            autocomplete="family-name"
                            @input="checkValidationAndUpdate('lname')"
                        />
                    </ValidationProvider>
                </div>
            </div>
            <div class="grid__col-sm-7">
                <label class="ls-label ls-margin--top" for="tel">Phone</label>
                <div class="ls-input__wrapper">
                    <ValidationProvider v-slot="{ classes }" ref="phone" rules="required|isPhone">
                        <span class="ls-input__icon" :class="classes" aria-hidden="true">
                            <i class="fa fa-phone fa-fw"></i>
                        </span>
                        <ls-cleave
                            id="tel"
                            v-model.trim="phone"
                            v-ls-track.change="{
                                target: 'contact-info--phone-input',
                                attachValue: true,
                            }"
                            :options="{
                                phone: true,
                                phoneRegionCode: 'US',
                                delimiter: '-',
                            }"
                            type="tel"
                            maxlength="50"
                            class="ls-input"
                            :class="classes"
                            name="tel"
                            autocomplete="tel"
                            @input="checkValidationAndUpdate('phone')"
                        ></ls-cleave>
                    </ValidationProvider>
                </div>
            </div>
            <div class="grid__col-sm-7">
                <label class="ls-label ls-margin--top" for="email">
                    Email
                </label>
                <div class="ls-input__wrapper">
                    <ValidationProvider v-slot="{ classes }" ref="email" rules="required|email">
                        <span class="ls-input__icon" :class="classes" aria-hidden="true">
                            <i class="fa fa-envelope-o fa-fw"></i>
                        </span>
                        <input
                            id="email"
                            v-model.trim="email"
                            v-ls-track.change="{
                                target: 'contact-info--email-input',
                                attachValue: true,
                            }"
                            type="email"
                            maxlength="50"
                            class="ls-input"
                            :class="classes"
                            name="email"
                            autocomplete="email"
                            @input="checkValidationAndUpdate('email')"
                        />
                    </ValidationProvider>
                </div>
                <div
                    v-if="!title"
                    style="margin-top: 2px;"
                    data-font-size="-2"
                    class="ls-label__helper-text ls-color--gray--light"
                >
                    This will be your login, too.
                </div>
            </div>
        </div>
    </ValidationObserver>
</template>

<script>
/** Vue2js Contact Info component.
 * Example usage:
 * <ls-contact-info
 *     :title="title"                  // (optional) String: Title for component. Default: "Create your account"
 * ></ls-contact-info>
 *
 */

import _ from 'lodash';
import { mapState, mapActions } from 'vuex';
import * as types from '@/store/types';
import eventBusService from '@/services/event-bus.service';
import lsCleave from '@/components/original-mydot/inputs/cleave/Cleave.vue';
import brandMixin from '@/mixins/brand.mixin';

export default {
    components: {
        lsCleave,
    },
    mixins: [brandMixin],
    props: {
        title: { type: String, default: null },
    },
    data() {
        return {
            fname: '',
            lname: '',
            phone: '',
            email: '',
        };
    },
    computed: {
        ...mapState(['order']),
    },
    created() {
        this.fname = _.get(this.order, 'fname') || this.fname;
        this.lname = _.get(this.order, 'lname') || this.lname;
        this.phone = _.get(this.order, 'phone') || this.phone;
        this.email = _.get(this.order, 'email') || this.email;
    },
    mounted() {
        // called on all 4 to ensure their values are pushed into the store
        this.checkValidationAndUpdate('fname');
        this.checkValidationAndUpdate('lname');
        this.checkValidationAndUpdate('phone');
        this.checkValidationAndUpdate('email');

        eventBusService.eventBus.$on('submitButtonClick', () => {
            if (this.$refs.contactInfo) {
                this.$refs.contactInfo.validate();
            }
        });
    },
    methods: {
        ...mapActions({
            updateFirstName: types.CONTACT_INFO__UPDATE_FIRST_NAME,
            updateLastName: types.CONTACT_INFO__UPDATE_LAST_NAME,
            updatePhone: types.CONTACT_INFO__UPDATE_PHONE,
            updateEmail: types.CONTACT_INFO__UPDATE_EMAIL,
            updateContactInfoValidation: types.CONTACT_INFO__UPDATE_VALIDATION,
        }),
        checkValidationAndUpdate(ref) {
            this.$nextTick(() => {
                this.$refs.contactInfo.validate({ silent: true }).then((valid) => {
                    this.updateContactInfoValidation({ valid });
                });

                if (ref === 'fname') {
                    this.updateFirstName({ fname: this.fname });
                } else if (ref === 'lname') {
                    this.updateLastName({ lname: this.lname });
                } else if (ref === 'phone') {
                    this.updatePhone({ phone: this.phone });
                } else {
                    this.updateEmail({ email: this.email });
                }
            });
        },
    },
};
</script>

<style lang="scss"></style>
